import { ButtonContent } from '../../../ui/ButtonContent';
/**
 * Generic "connect a provider" row for integration settings sections.
 *
 * Rendered in the dashed-border "available to connect" idiom below the list of
 * connected accounts: a provider glyph, its name, and a quiet Connect action.
 * Any future integration type reuses this primitive so connecting looks the
 * same everywhere. The markup mirrors the available-to-link rows in
 * AccountSection.
 *
 * @module components/admin/settings/integrations/ProviderConnectButton
 */
import React from 'react';

/** Props for a provider connect row. */
export interface ProviderConnectButtonProps {
  /** Provider glyph rendered on the left. */
  icon: React.ReactNode;
  /** Provider name shown next to the glyph. */
  label: string;
  /** Invoked when the user starts the connect flow. */
  onConnect: () => void | Promise<void>;
  /** Whether a connect flow is already in progress (disables the action). */
  busy?: boolean;
}

/**
 * A dashed-border row inviting the user to connect a not-yet-connected
 * provider. The label names the provider; the action always reads "Connect".
 */
export const ProviderConnectButton: React.FC<ProviderConnectButtonProps> = ({
  icon,
  label,
  onConnect,
  busy = false,
}) => (
  <div className="flex items-center justify-between p-3 bg-background border border-border/50 border-dashed rounded-lg">
    <div className="flex items-center gap-3 min-w-0">
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
    <button aria-busy={busy}
      type="button"
      onClick={() => void onConnect()}
      disabled={busy}
      className="text-xs text-volt-soft light:text-volt hover:text-volt-soft transition-colors disabled:opacity-50 shrink-0 ml-3"
    >
      <ButtonContent loading={busy}>Connect</ButtonContent>
    </button>
  </div>
);
